import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import {
  ECategoryType,
  type IAdminProduct,
  type IDimensions,
  type IProductFeatureFlag,
  type IReportWarehouseProductToImport,
  type IWarehouseProductCategorySuggestion,
  type IWarehouseProductSuggestions,
} from '@mahawi/eshop-common/src/types';
import { type UnknownAction } from '@reduxjs/toolkit';
import {
  Alert,
  Button,
  Flex,
  Progress,
  Select,
  Space,
  Switch,
  Tooltip,
  Typography,
} from 'antd';
import { type ColumnsType } from 'antd/es/table';
import {
  type FilterValue,
  type TableCurrentDataSource,
} from 'antd/es/table/interface';
import React, {
  Key,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { type Socket } from 'socket.io-client';

import { ENotificationSeverity } from '../../admin-types';
import DetailPageHeader from '../components/detail-page-header';
import ExternalLink from '../components/external-link';
import Link from '../components/link';
import Table from '../components/table';
import WarehouseProductFilter from '../components/warehouse-product/filter';
import { EMPTY_VALUE } from '../fragments/empty-value';
import { featureFlagTags } from '../fragments/feature-flag-tags';
import { normalizeWarehouseProductName } from '../fragments/normalize-warehouse-product-name';
import { type ProductTableColumn } from '../fragments/products-table-columns';
import { getFilterOption, getFilterSort } from '../fragments/select-filter';
import {
  getTableFeatureFlagsColumnFilters,
  getTableFeatureFlagsColumnOnFilter,
} from '../fragments/table-feature-flags-column';
import { getColumnSearch } from '../fragments/table-get-column-search';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { useNotify } from '../notification';
import { type RootState } from '../reducers';
import {
  downloaderAddWarehouseProductAI,
  downloaderAddWarehouseProductAILocal,
  downloaderAddWarehouseProductResponse,
} from '../reducers/downloader/reducer';
import {
  reportWarehouseProductCategorySuggestionLoad,
  reportWarehouseProductCategorySuggestionLoadResponse,
  reportWarehouseProductChildCategoryEstimateLoad,
  reportWarehouseProductChildCategoryEstimateLoadResponse,
  reportWarehouseProductToImportLoad,
} from '../reducers/report/reducer';
import { isRequestFailed } from '../reducers/request-failed/reducer';
import { sendRaw } from '../reducers/send-request';
import { getSocket } from '../saga';
import { SPACING } from '../theme/tokens';

const { Text } = Typography;

interface DataType extends IReportWarehouseProductToImport {
  key: string;
}

const UNMAPPED_CATEGORY_LABEL = 'Unmapped';

const BATCH_ITEM_ACK_TIMEOUT_MS = 5 * 60 * 1000;
const BATCH_RECONNECT_WAIT_MS = 60 * 1000;

enum EBatchItemStatus {
  DONE = 'done',
  FAILED = 'failed',
  TIMED_OUT = 'timedOut',
  DISCONNECTED = 'disconnected',
}

interface IBatchItemResult {
  status: EBatchItemStatus;
  response?: ReturnType<typeof downloaderAddWarehouseProductResponse>;
}

const sendBatchItem = (
  socket: Socket,
  action:
    | ReturnType<typeof downloaderAddWarehouseProductAI>
    | ReturnType<typeof downloaderAddWarehouseProductAILocal>,
): Promise<IBatchItemResult> =>
  new Promise((resolve): void => {
    let resolved = false;
    let onDisconnect: () => void = (): void => {};

    const finish = (result: IBatchItemResult): void => {
      if (resolved) {
        return;
      }

      resolved = true;
      socket.off('disconnect', onDisconnect);
      resolve(result);
    };

    onDisconnect = (): void => {
      finish({ status: EBatchItemStatus.DISCONNECTED });
    };

    socket.once('disconnect', onDisconnect);

    socket
      .timeout(BATCH_ITEM_ACK_TIMEOUT_MS)
      .emit(
        action.type,
        action,
        (
          error: Error | null,
          response: ReturnType<typeof downloaderAddWarehouseProductResponse>,
        ): void => {
          if (error) {
            finish({ status: EBatchItemStatus.TIMED_OUT });
            return;
          }

          finish({
            status: isRequestFailed(response as UnknownAction)
              ? EBatchItemStatus.FAILED
              : EBatchItemStatus.DONE,
            response,
          });
        },
      );
  });

const waitForSocketConnection = (socket: Socket): Promise<boolean> =>
  new Promise((resolve): void => {
    if (socket.connected) {
      resolve(true);
      return;
    }

    const reconnectTimer: {
      id?: ReturnType<typeof globalThis.setTimeout>;
    } = {};

    const onConnect = (): void => {
      globalThis.clearTimeout(reconnectTimer.id);
      resolve(true);
    };

    reconnectTimer.id = globalThis.setTimeout((): void => {
      socket.off('connect', onConnect);
      resolve(socket.connected);
    }, BATCH_RECONNECT_WAIT_MS);

    socket.once('connect', onConnect);
    socket.connect();
  });

const getUnmappedCategoryLabel = (record: DataType): string =>
  record.warehouseCategoryNames.length > 0
    ? record.warehouseCategoryNames.join(', ')
    : UNMAPPED_CATEGORY_LABEL;

const getCategoryLabel = (record: DataType): string =>
  record.category?.name ?? getUnmappedCategoryLabel(record);

const formatSuggestionLabel = ({
  path,
  score,
}: IWarehouseProductCategorySuggestion): string =>
  `${path.join(' / ')} (${Math.round(score * 100)} %)`;

const STOCK_FILTER_OPTIONS: { text: string; value: string }[] = [
  { text: 'In stock', value: 'inStock' },
  { text: 'Full stock', value: 'fullStock' },
  { text: 'Low stock', value: 'lowStock' },
];

// Single source of truth for column onFilter predicates, reused to derive
// each column's own cross-filtered option list (see applyFiltersExcept).
const FILTER_MATCHERS: Record<
  string,
  (value: boolean | Key, record: DataType) => boolean
> = {
  category: (value: boolean | Key, record: DataType): boolean =>
    getCategoryLabel(record) === value,
  brand: (value: boolean | Key, record: DataType): boolean =>
    record.brand === value,
  count: (value: boolean | Key, record: DataType): boolean => {
    if (value === 'inStock') {
      return record.count > 0;
    }
    if (value === 'fullStock') {
      return record.count >= 5;
    }
    if (value === 'lowStock') {
      return record.count > 0 && record.count < 5;
    }
    return false;
  },
  featureFlags: getTableFeatureFlagsColumnOnFilter,
};

// Returns rows matching every currently active filter except `exceptKey`, so
// that column's own filter dropdown only offers options still reachable
// given what's already selected in the other columns.
function applyFiltersExcept(
  data: DataType[],
  activeFilters: Record<string, FilterValue | null>,
  exceptKey: string,
): DataType[] {
  const activeEntries = Object.entries(activeFilters).filter(
    ([key, values]): boolean =>
      key !== exceptKey && !!values && values.length > 0,
  );

  if (activeEntries.length === 0) {
    return data;
  }

  return data.filter((record: DataType): boolean =>
    activeEntries.every(([key, values]): boolean => {
      const matcher = FILTER_MATCHERS[key];
      return (
        !matcher ||
        (values as FilterValue).some((value) => matcher(value, record))
      );
    }),
  );
}

function ReportWarehouseProductToImportContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const notify = useNotify();
  const reportWarehouseProductToImport = useSelector(
    (s: RootState) => s.Report.warehouseProductToImport,
  );
  const reportUpdatedAt = useSelector((s: RootState) => s.Report.updatedAt);
  const reportInProcess = useSelector((s: RootState) => s.Report.inProcess);
  const reportWarehouseProductChildCategoryEstimates = useSelector(
    (s: RootState) => s.Report.warehouseProductChildCategoryEstimates,
  );
  const childCategorySearchUnavailable = useSelector(
    (s: RootState) => s.Report.warehouseProductChildCategorySearchUnavailable,
  );
  const reportWarehouseProductCategorySuggestions = useSelector(
    (s: RootState) => s.Report.warehouseProductCategorySuggestions,
  );
  const featureFlagsState = useSelector((s: RootState) => s.FeatureFlags);
  const warehouseState = useSelector((s: RootState) => s.Warehouse);

  const dataSource = useMemo((): DataType[] => {
    if (!reportWarehouseProductToImport) {
      return [];
    }

    return reportWarehouseProductToImport.map(
      (product: IReportWarehouseProductToImport): DataType => ({
        key: product.uuid,
        ...product,
      }),
    );
  }, [reportWarehouseProductToImport]);

  const [activeFilters, setActiveFilters] = useState<
    Record<string, FilterValue | null>
  >({ featureFlags: ['withoutFeatureFlags'] });

  const [categoryType, setCategoryType] = useState<ECategoryType>(
    ECategoryType.CHILD,
  );
  const [excludeStaleProducts, setExcludeStaleProducts] =
    useState<boolean>(true);
  const [includeUnmappedCategories, setIncludeUnmappedCategories] =
    useState<boolean>(false);
  const [currentWarehouseUUID, setCurrentWarehouseUUID] = useState<
    string | null
  >(null);
  const [confirmedCategories, setConfirmedCategories] = useState<
    Record<string, string[]>
  >({});
  const [isEstimating, setIsEstimating] = useState(false);
  const [isSuggesting, setIsSuggesting] = useState(false);

  const wrappedCb = useCallback(
    (
      warehouseUuid: string,
      minimalWeight: number,
      maximalWeight: number,
      minimalPrice: number,
      maximalPrice: number,
      minimalDescriptionLength: number,
    ): UnknownAction => {
      setCurrentWarehouseUUID(warehouseUuid);
      return reportWarehouseProductToImportLoad(
        warehouseUuid,
        minimalWeight,
        maximalWeight,
        minimalPrice,
        maximalPrice,
        minimalDescriptionLength,
        categoryType,
        excludeStaleProducts,
        includeUnmappedCategories,
      );
    },
    [categoryType, excludeStaleProducts, includeUnmappedCategories],
  );

  const [filteredDataSource, setFilteredDataSource] = useState<DataType[]>([]);

  useEffect((): void => {
    setFilteredDataSource(
      dataSource.filter((r) => r.featureFlags.length === 0),
    );
  }, [dataSource]);

  const handleTableChange = useCallback(
    (
      _p: object,
      filters: Record<string, FilterValue | null>,
      _s: object,
      extra: TableCurrentDataSource<object>,
    ): void => {
      setActiveFilters(filters);
      setFilteredDataSource(extra.currentDataSource as DataType[]);
    },
    [],
  );

  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [isProcessingBatch, setIsProcessingBatch] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const [totalToProcess, setTotalToProcess] = useState(0);
  const [addedProducts, setAddedProducts] = useState<IAdminProduct[]>([]);
  const cancelledRef = useRef(false);

  const handleReset = useCallback((): void => {
    cancelledRef.current = true;
    setSelectedRowKeys([]);
    setIsProcessingBatch(false);
    setProcessedCount(0);
    setTotalToProcess(0);
    setAddedProducts([]);
  }, []);

  const runBatch = useCallback(
    async (
      actionCreator:
        | typeof downloaderAddWarehouseProductAI
        | typeof downloaderAddWarehouseProductAILocal,
    ): Promise<void> => {
      const socket = getSocket();
      const toProcess = dataSource.filter((r) =>
        selectedRowKeys.includes(r.key),
      );

      cancelledRef.current = false;
      setIsProcessingBatch(true);
      setProcessedCount(0);
      setTotalToProcess(toProcess.length);
      setAddedProducts([]);

      for (let i = 0; i < toProcess.length; i++) {
        if (cancelledRef.current) break;

        const record = toProcess[i];

        const overrideCategoryUUIDs: string[] | undefined = confirmedCategories[
          record.uuid
        ]?.length
          ? confirmedCategories[record.uuid]
          : undefined;

        const action = actionCreator(
          record.uuid,
          normalizeWarehouseProductName(record.name),
          overrideCategoryUUIDs,
        );

        const isConnected: boolean = await waitForSocketConnection(socket);

        if (!isConnected) {
          notify(
            ENotificationSeverity.ERROR,
            'Batch add stopped',
            `Connection to the server was lost and did not recover. Processed ${i} of ${toProcess.length} products — reload the page and run the batch again for the rest.`,
          );
          setIsProcessingBatch(false);
          return;
        }

        const result: IBatchItemResult = await sendBatchItem(socket, action);

        if (result.status === EBatchItemStatus.FAILED) {
          dispatch(result.response as UnknownAction);
        }

        if (
          result.status === EBatchItemStatus.TIMED_OUT ||
          result.status === EBatchItemStatus.DISCONNECTED
        ) {
          notify(
            ENotificationSeverity.WARNING,
            'Server response lost',
            `No response for "${record.name}" (${result.status}). It may still have been added — check it after the batch. Continuing with the next product.`,
          );
        }

        const addedProduct: IAdminProduct | null | undefined =
          result.status === EBatchItemStatus.DONE
            ? result.response?.payload?.product
            : undefined;

        if (!cancelledRef.current && addedProduct) {
          setAddedProducts((prev) => [...prev, addedProduct]);
        }

        if (cancelledRef.current) break;

        setProcessedCount((prev) => prev + 1);

        // Give the backend time to commit the transaction and fire afterCommit
        // hooks (rabbit publishes) before starting the next product, to avoid
        // concurrent channel conflicts on the rabbit layer.
        if (i < toProcess.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      }

      if (!cancelledRef.current) {
        setIsProcessingBatch(false);
        setSelectedRowKeys([]);
      }
    },
    [dataSource, selectedRowKeys, confirmedCategories, dispatch, notify],
  );

  const handleAddWithAI = useCallback(
    () => runBatch(downloaderAddWarehouseProductAI),
    [runBatch],
  );

  const handleAddWithAILocal = useCallback(
    () => runBatch(downloaderAddWarehouseProductAILocal),
    [runBatch],
  );

  const handleEstimateChildCategories = useCallback(async (): Promise<void> => {
    if (!currentWarehouseUUID || !filteredDataSource.length) return;
    const socket = getSocket();
    setIsEstimating(true);
    try {
      const action = reportWarehouseProductChildCategoryEstimateLoad(
        filteredDataSource.map((r) => r.uuid),
        currentWarehouseUUID,
      );
      const response = await sendRaw(socket, action);
      if (response) {
        dispatch(
          response as ReturnType<
            typeof reportWarehouseProductChildCategoryEstimateLoadResponse
          >,
        );
      }
    } finally {
      setIsEstimating(false);
    }
  }, [currentWarehouseUUID, filteredDataSource, dispatch]);

  const handleSuggestCategories = useCallback(async (): Promise<void> => {
    if (!filteredDataSource.length) return;
    const socket = getSocket();
    setIsSuggesting(true);
    try {
      const response = await sendRaw(
        socket,
        reportWarehouseProductCategorySuggestionLoad(
          filteredDataSource.map((r) => r.uuid),
        ),
      );
      if (response) {
        dispatch(
          response as ReturnType<
            typeof reportWarehouseProductCategorySuggestionLoadResponse
          >,
        );
      }
    } finally {
      setIsSuggesting(false);
    }
  }, [filteredDataSource, dispatch]);

  useEffect((): void => {
    handleReset();
    setConfirmedCategories({});
    dispatch(reportWarehouseProductCategorySuggestionLoadResponse([]));
  }, [reportWarehouseProductToImport]);

  useEffect((): void => {
    if (!reportWarehouseProductCategorySuggestions?.length) return;
    setConfirmedCategories((prev) => {
      const next: Record<string, string[]> = { ...prev };
      for (const suggestion of reportWarehouseProductCategorySuggestions) {
        const recommended: string[] = suggestion.suggestedCategories
          .filter(
            ({ isRecommended }: IWarehouseProductCategorySuggestion): boolean =>
              isRecommended,
          )
          .map(({ uuid }: IWarehouseProductCategorySuggestion): string => uuid);
        if (
          recommended.length > 0 &&
          !next[suggestion.warehouseProductUuid]?.length
        ) {
          next[suggestion.warehouseProductUuid] = recommended;
        }
      }
      return next;
    });
  }, [reportWarehouseProductCategorySuggestions]);

  const aiSuggestionMap = useMemo(
    () =>
      new Map(
        (reportWarehouseProductCategorySuggestions ?? [])
          .filter(
            (suggestion: IWarehouseProductSuggestions): boolean =>
              suggestion.suggestedCategories.length > 0,
          )
          .map((suggestion: IWarehouseProductSuggestions) => [
            suggestion.warehouseProductUuid,
            suggestion.suggestedCategories,
          ]),
      ),
    [reportWarehouseProductCategorySuggestions],
  );

  useEffect((): void => {
    if (!reportWarehouseProductChildCategoryEstimates) return;
    const initial: Record<string, string[]> = {};
    for (const est of reportWarehouseProductChildCategoryEstimates) {
      if (est.suggestedChildCategories.length > 0) {
        initial[est.warehouseProductUUID] = est.suggestedChildCategories.map(
          (c) => c.uuid,
        );
      }
    }
    setConfirmedCategories(initial);
  }, [reportWarehouseProductChildCategoryEstimates]);

  const estimateMap = useMemo(
    () =>
      new Map(
        (reportWarehouseProductChildCategoryEstimates ?? []).map((e) => [
          e.warehouseProductUUID,
          e,
        ]),
      ),
    [reportWarehouseProductChildCategoryEstimates],
  );

  const columns = useMemo((): ColumnsType<object> => {
    const categoryFilterOptions = [
      ...new Set(
        applyFiltersExcept(dataSource, activeFilters, 'category').map(
          (record: DataType): string => getCategoryLabel(record),
        ),
      ),
    ]
      .map((category: string) => ({ text: category, value: category }))
      .sort((a, b): number => stringCompare(a.text, b.text));

    const brandFilterOptions = [
      ...new Set(
        applyFiltersExcept(dataSource, activeFilters, 'brand').map(
          (record: DataType): string => record.brand,
        ),
      ),
    ]
      .map((brand: string) => ({ text: brand, value: brand }))
      .sort((a, b): number => stringCompare(a.text, b.text));

    const countFilterSubset = applyFiltersExcept(
      dataSource,
      activeFilters,
      'count',
    );
    const countFilterOptions = STOCK_FILTER_OPTIONS.filter((option) =>
      countFilterSubset.some((record: DataType): boolean =>
        FILTER_MATCHERS.count(option.value, record),
      ),
    );

    const featureFlagsFilterSubset = applyFiltersExcept(
      dataSource,
      activeFilters,
      'featureFlags',
    );
    const featureFlagsFilterOptions = getTableFeatureFlagsColumnFilters({
      dataSource: featureFlagsFilterSubset,
      FeatureFlags: featureFlagsState,
    }).filter((option) =>
      featureFlagsFilterSubset.some((record: DataType): boolean =>
        FILTER_MATCHERS.featureFlags(option.value as boolean | Key, record),
      ),
    );

    const baseColumns: ColumnsType<object> = [
      {
        title: 'Category',
        dataIndex: 'category',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(getCategoryLabel(a), getCategoryLabel(b)),
        filterSearch: true,
        filters: categoryFilterOptions,
        onFilter: FILTER_MATCHERS.category,
        render: (_: DataType['category'], record: DataType): React.ReactNode =>
          record.category ? (
            record.category.name
          ) : (
            <Tooltip title="Supplier category not mapped to any eshop category">
              <Text type="warning">{getUnmappedCategoryLabel(record)}</Text>
            </Tooltip>
          ),
        width: 160,
      },
      {
        title: 'Brand',
        dataIndex: 'brand',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.brand, b.brand),
        filterSearch: true,
        filters: brandFilterOptions,
        onFilter: FILTER_MATCHERS.brand,
        width: 120,
      },
      {
        title: 'Name',
        dataIndex: 'name',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.name, b.name),
        render: (name: string, record: DataType): React.ReactElement => (
          <Space size={SPACING.xs} align="start">
            <Link
              to={`/downloader/warehouse-product/${record.uuid}`}
              label={name}
              openInNewTab
            />
            {record.url && (
              <ExternalLink
                to={record.url}
                ariaLabel="Open warehouse URL in new tab"
              />
            )}
          </Space>
        ),
        ...getColumnSearch('name'),
      },
      {
        title: 'Price',
        dataIndex: 'price',
        sorter: {
          compare: (a: DataType, b: DataType): number => a.price - b.price,
          multiple: 100,
        },
        width: 90,
      },
      {
        title: 'Stock',
        dataIndex: 'count',
        sorter: {
          compare: (a: DataType, b: DataType): number => a.count - b.count,
          multiple: 3,
        },
        filterSearch: true,
        filters: countFilterOptions,
        onFilter: FILTER_MATCHERS.count,
        render: (value: number): React.ReactElement => {
          if (value >= 5) {
            return (
              <Text strong type="success">
                {value}
              </Text>
            );
          }
          if (value >= 2) {
            return (
              <Text strong type="warning">
                {value}
              </Text>
            );
          }

          return (
            <Text strong type="danger">
              {value}
            </Text>
          );
        },
        width: 80,
      },
      {
        title: 'Feature flags',
        dataIndex: 'featureFlags',
        sorter: (a: ProductTableColumn, b: ProductTableColumn): number =>
          a.featureFlags.length - b.featureFlags.length,
        filterSearch: true,
        filters: featureFlagsFilterOptions,
        onFilter: FILTER_MATCHERS.featureFlags,
        render: (
          productFeatureFlags: IProductFeatureFlag[],
        ): React.ReactElement[] =>
          featureFlagTags({
            productFeatureFlags,
            FeatureFlags: featureFlagsState,
            Warehouse: warehouseState,
          }),
        width: 140,
        defaultFilteredValue: ['withoutFeatureFlags'],
      },
      {
        title: 'MPN',
        dataIndex: 'mpn',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.mpn, b.mpn),
        filterSearch: true,
        onFilter: (value: boolean | Key, record: DataType): boolean =>
          record.mpn === value,
        width: 140,
      },
      {
        title: 'GTIN',
        dataIndex: 'gtin',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.gtin, b.gtin),
        filterSearch: true,
        onFilter: (value: boolean | Key, record: DataType): boolean =>
          record.gtin === value,
        width: 140,
      },
      {
        title: 'Weight',
        dataIndex: 'dimensions',
        sorter: {
          compare: (a: DataType, b: DataType): number =>
            a.dimensions.weight - b.dimensions.weight,
          multiple: 3,
        },
        render: (dimensions: IDimensions): string => `${dimensions.weight} kg`,
        width: 90,
      },
    ];

    if (
      (categoryType === ECategoryType.PARENT && estimateMap.size > 0) ||
      aiSuggestionMap.size > 0
    ) {
      baseColumns.push({
        title: 'Suggested Category',
        key: 'suggestedCategory',
        dataIndex: 'uuid',
        render: (_: string, record: DataType): React.ReactElement => {
          const est = estimateMap.get(record.uuid);
          const aiSuggestions: IWarehouseProductCategorySuggestion[] =
            aiSuggestionMap.get(record.uuid) ?? [];
          const options: { value: string; label: string }[] = [
            ...aiSuggestions.map(
              (suggestion: IWarehouseProductCategorySuggestion) => ({
                value: suggestion.uuid,
                label: formatSuggestionLabel(suggestion),
              }),
            ),
            ...(categoryType === ECategoryType.PARENT && est
              ? est.allChildCategories
              : []
            )
              .filter(
                (c): boolean =>
                  !aiSuggestions.some(
                    ({ uuid }: IWarehouseProductCategorySuggestion): boolean =>
                      uuid === c.uuid,
                  ),
              )
              .map((c) => ({ value: c.uuid, label: c.name })),
          ];
          if (options.length === 0) {
            return <span>{EMPTY_VALUE}</span>;
          }
          return (
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              value={confirmedCategories[record.uuid] ?? []}
              onChange={(val: string[]): void =>
                setConfirmedCategories((prev) => ({
                  ...prev,
                  [record.uuid]: val,
                }))
              }
              allowClear
              placeholder="Select categories"
              showSearch={{
                filterOption: getFilterOption,
                filterSort: getFilterSort,
              }}
              options={options}
            />
          );
        },
        width: 360,
      });
    }

    return baseColumns;
  }, [
    dataSource,
    activeFilters,
    reportUpdatedAt,
    featureFlagsState.featureFlags,
    featureFlagsState,
    warehouseState,
    categoryType,
    estimateMap,
    aiSuggestionMap,
    confirmedCategories,
  ]);

  return (
    <Flex vertical gap={SPACING.lg} style={{ width: '100%' }}>
      <DetailPageHeader title="Report warehouse product to import" />

      {childCategorySearchUnavailable && (
        <Alert
          type="warning"
          showIcon
          title="Search is temporarily unavailable — subcategory suggestions were estimated without search."
        />
      )}

      {includeUnmappedCategories && (
        <Alert
          type="info"
          showIcon
          title="Unmapped products are created without categories unless a category is selected in the Suggested Category column."
        />
      )}

      <Space size={SPACING.lg} align="center">
        <Switch
          checkedChildren="Child"
          unCheckedChildren="Parent (L1)"
          defaultChecked
          onChange={(checked: boolean): void =>
            setCategoryType(
              checked ? ECategoryType.CHILD : ECategoryType.PARENT,
            )
          }
        />
        <Tooltip title="Hide products whose last warehouse sync is more than 1 day old — their stock count is likely stale and no longer accurate">
          <Switch
            checkedChildren="Recently synced only"
            unCheckedChildren="Include stale"
            checked={excludeStaleProducts}
            onChange={setExcludeStaleProducts}
          />
        </Tooltip>
        <Tooltip title="Also list products whose warehouse categories are not mapped to any eshop category">
          <Switch
            checkedChildren="Include unmapped"
            unCheckedChildren="Mapped only"
            checked={includeUnmappedCategories}
            onChange={setIncludeUnmappedCategories}
          />
        </Tooltip>
        <WarehouseProductFilter cb={wrappedCb} />
        <Tooltip
          title={
            categoryType !== ECategoryType.PARENT
              ? 'Switch to Parent (L1) mode to estimate'
              : filteredDataSource.length === 0
                ? 'No visible products to estimate'
                : undefined
          }
        >
          <Button
            loading={isEstimating}
            disabled={
              isEstimating ||
              categoryType !== ECategoryType.PARENT ||
              filteredDataSource.length === 0
            }
            onClick={handleEstimateChildCategories}
          >
            Estimate child categories ({filteredDataSource.length})
          </Button>
        </Tooltip>
        <Tooltip
          title={
            filteredDataSource.length === 0
              ? 'No visible products to suggest categories for'
              : 'Suggest categories from the most similar existing products'
          }
        >
          <Button
            loading={isSuggesting}
            disabled={isSuggesting || filteredDataSource.length === 0}
            onClick={handleSuggestCategories}
          >
            Suggest categories (AI) ({filteredDataSource.length})
          </Button>
        </Tooltip>
      </Space>

      <Table
        key={reportWarehouseProductToImport ? 'loaded' : 'loading'}
        columns={columns}
        dataSource={dataSource}
        isLoading={reportInProcess}
        emptyText="No products to import"
        rowSelection={{
          type: 'checkbox',
          columnWidth: 48,
          selectedRowKeys,
          onChange: (keys: React.Key[]): void =>
            setSelectedRowKeys(keys as string[]),
        }}
        onChange={handleTableChange}
      />

      <Flex vertical gap={SPACING.sm} style={{ width: '100%' }}>
        <Flex gap={SPACING.sm}>
          <Button
            loading={isProcessingBatch}
            disabled={isProcessingBatch || selectedRowKeys.length === 0}
            onClick={handleAddWithAI}
          >
            Add{selectedRowKeys.length > 0 ? ` ${selectedRowKeys.length}` : ''}{' '}
            products with AI
          </Button>

          <Button
            type="primary"
            loading={isProcessingBatch}
            disabled={isProcessingBatch || selectedRowKeys.length === 0}
            onClick={handleAddWithAILocal}
          >
            Add{selectedRowKeys.length > 0 ? ` ${selectedRowKeys.length}` : ''}{' '}
            products with AI (local)
          </Button>

          {(selectedRowKeys.length > 0 ||
            addedProducts.length > 0 ||
            isProcessingBatch) && <Button onClick={handleReset}>Reset</Button>}
        </Flex>

        {isProcessingBatch && (
          <Progress
            percent={Math.round((processedCount / totalToProcess) * 100)}
            status="active"
            format={() => `${processedCount} / ${totalToProcess}`}
          />
        )}

        {addedProducts.length > 0 && (
          <Flex vertical gap={SPACING.xs} style={{ width: '100%' }}>
            <Text strong>Added products ({addedProducts.length}):</Text>
            {addedProducts.map((product) => (
              <Link
                key={product.uuid}
                to={`/product/${product.uuid}`}
                label={product.names[0]?.value ?? product.uuid}
                openInNewTab
                additionalSearchParams={{
                  productCategories: 'open',
                  productLanguage: 'open',
                }}
              />
            ))}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}

export default ReportWarehouseProductToImportContainer;
